import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useSession } from './useSession'
import { getCurrentUserByAuthProviderId } from '../services/users'
import { identifyUser } from '../lib/analytics'
import { SPECIAL_SCHEMA, SPECIAL_TABLES } from '../lib/specialSchema'
import { logErrorToService } from '../utils/errorLogger'
import type { Database } from '../types/database.generated'

type User = Database[typeof SPECIAL_SCHEMA]['Tables'][typeof SPECIAL_TABLES.users]['Row']

const mappedUserCache = new Map<string, User>()
const mappedUserInFlight = new Map<string, Promise<User | null>>()
const CURRENT_USER_RETRY_DELAYS_MS = [150, 400, 900]

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function getCacheKey(authProviderUserId: string, sessionId: string | null): string {
  return `${authProviderUserId}::${sessionId ?? 'no-session'}`
}

async function getMappedUserWithRetry(authProviderUserId: string, sessionId: string | null): Promise<User | null> {
  const cacheKey = getCacheKey(authProviderUserId, sessionId)

  if (mappedUserCache.has(cacheKey)) {
    return mappedUserCache.get(cacheKey) ?? null
  }

  const inFlight = mappedUserInFlight.get(cacheKey)
  if (inFlight) {
    return inFlight
  }

  const request = (async () => {
    const attempts = CURRENT_USER_RETRY_DELAYS_MS.length + 1

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const user = await getCurrentUserByAuthProviderId(authProviderUserId)
      if (user) {
        mappedUserCache.set(cacheKey, user)

        if (attempt > 0) {
          Sentry.addBreadcrumb({
            category: 'auth-bootstrap',
            level: 'info',
            message: 'Current user resolved after startup retry',
            data: {
              attempt: attempt + 1,
              authProviderUserId,
              sessionId: sessionId ?? 'no-session',
            },
          })
        }

        return user
      }

      if (attempt < CURRENT_USER_RETRY_DELAYS_MS.length) {
        await wait(CURRENT_USER_RETRY_DELAYS_MS[attempt])
      }
    }

    return null
  })()
    .finally(() => {
      mappedUserInFlight.delete(cacheKey)
    })

  mappedUserInFlight.set(cacheKey, request)
  return request
}

export const CURRENT_USER_ERROR_AUTH_PROVIDER_NOT_MAPPED = 'auth_user_not_mapped'

interface CurrentUserState {
  isLoading: boolean
  user: User | null
  userId: string | null
  authProviderUserId: string | null
  error: string | null
}

/**
 * Loads the full user row from the shared users table for the currently
 * logged-in user. It resolves the row via `auth_provider_id` using
 * the provider id from `useSession`.
 */
export function useCurrentUser(): CurrentUserState {
  const { isAuthenticated, isTokenReady, userId, sessionId } = useSession()
  const [state, setState] = useState<CurrentUserState>({
    isLoading: false,
    user: null,
    userId: null,
    authProviderUserId: null,
    error: null,
  })

  useEffect(() => {
    let cancelled = false

    if (isAuthenticated && !isTokenReady) {
      queueMicrotask(() => {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isLoading: true,
            authProviderUserId: userId ?? prev.authProviderUserId,
            error: null,
          }))
        }
      })

      return () => { cancelled = true }
    }

    if (!isAuthenticated || !userId) {
      mappedUserCache.clear()
      mappedUserInFlight.clear()
      Sentry.setUser(null)

      queueMicrotask(() => {
        if (!cancelled) {
          setState({
            isLoading: false,
            user: null,
            userId: null,
            authProviderUserId: null,
            error: null,
          })
        }
      })
      return () => { cancelled = true }
    }

    queueMicrotask(() => {
      if (!cancelled) {
        setState((prev) => ({
          isLoading: true,
          user: prev.user,
          userId: prev.userId,
          authProviderUserId: userId,
          error: null,
        }))
      }
    })

    getMappedUserWithRetry(userId, sessionId)
      .then((user) => {
        if (cancelled) return

        if (!user) {
          setState({
            isLoading: false,
            user: null,
            userId: null,
            authProviderUserId: userId,
            error: CURRENT_USER_ERROR_AUTH_PROVIDER_NOT_MAPPED,
          })
          return
        }

        setState({
          isLoading: false,
          user,
          userId: user.id,
          authProviderUserId: userId,
          error: null,
        })
        Sentry.setUser({ id: user.id, email: user.email })
        identifyUser(user.email)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          logErrorToService(err, { context: 'useCurrentUser', severity: 'high' })
          const message = err instanceof Error ? err.message : 'Failed to load user data'
          setState({
            isLoading: false,
            user: null,
            userId: null,
            authProviderUserId: userId,
            error: message,
          })
        }
      })

    return () => { cancelled = true }
  }, [isAuthenticated, isTokenReady, sessionId, userId])

  return state
}
